import { Share } from '@promoboxx/graphql-gateway-types'
import {
  embedSocialPreview,
  SocialPreviewAdOptions,
  SocialPreviewAdOptionsCommon,
  BaseAdMedia,
} from '@promoboxx/social-preview'
import { useRef, useMemo, useEffect } from 'react'

import cloudinaryRef from '@src/lib/Cloudinary'

const getPreviewType = (channel: string) => {
  switch (channel) {
    case 'instagram':
      return 'instagram/single'
    case 'facebook':
      return 'facebook/single'
    case 'twitter':
      return 'twitter'
    default:
      return channel
  }
}

const buildSocialPreviewOptions = (
  share: Share,
): SocialPreviewAdOptions | undefined => {
  if (!share) {
    return
  }

  let media: BaseAdMedia[] = [],
    primaryText = '',
    shouldShowLink = false,
    userFullName = '',
    userHandle = '',
    userAvatar = '',
    shareLink = '',
    subject = undefined

  const previewType = getPreviewType(share?.channel || '')
  const [digitalAsset] =
    share?.share_setting?.digital_assets ||
    share?.share_setting?.digital_asset_associables ||
    []

  const isCarousel = share?.share_setting?.post_format === 'carousel'
  const isVideo =
    share?.share_setting?.post_format === 'video' ||
    !!digitalAsset?.external_video_url

  shouldShowLink =
    share?.share_setting?.share_url_type && !isVideo ? true : false

  // Build share link, accounting for landing page.
  if (share?.share_setting?.link) {
    if (share?.share_setting?.share_url_type === 'landing_page') {
      shareLink =
        share.share_setting.retailer_link ||
        `${share.share_setting.link}?share_setting_id=${share.share_setting?.id}`
    } else {
      shareLink = share.share_setting.link
    }
  }

  // Media carousel/single
  if (isCarousel && share?.share_setting?.child_attachments) {
    share?.share_setting?.child_attachments.forEach(
      (
        { link, description, title, call_to_action, media: mediaAttachment },
        index,
      ) => {
        const digitalAssetId = mediaAttachment?.source_id
        const { responsive_share_image_url, download_url } =
          (share?.share_setting?.digital_assets || []).find(
            (digitalAssetAssociables) => {
              return digitalAssetAssociables?.id === digitalAssetId
            },
          ) || {}

        media.push({
          callToAction: call_to_action || '',
          url: link || '',
          description: description || '',
          headline: title || '',
          mediaSrc: responsive_share_image_url || download_url || '',
          mediaType: 'image',
        })
      },
    )
  } else {
    const mediaType = isVideo ? 'video' : 'image'
    const src = isVideo
      ? digitalAsset?.external_video_url || digitalAsset?.download_url
      : share?.share_setting?.responsive_share_image_url
    if (previewType === 'facebook/single') {
      media.push({
        mediaType: isVideo ? 'video' : 'image',
        mediaSrc: src || '',
        callToAction: 'Learn More',
        url: shareLink || '',
        description: share?.share_setting?.retailer_description || '',
        headline: share?.share_setting?.retailer_title || '',
      })
    } else if (previewType === 'twitter') {
      media.push({
        mediaType,
        mediaSrc: src || '',
        url:
          share?.share_setting?.share_url_type === 'landing_page'
            ? `${share?.share_setting?.display_preview_url}?share_setting_id=${share?.share_setting?.id}`
            : share?.share_setting?.link || '',
      })
    } else if (previewType === 'email') {
      media.push({
        mediaType,
        mediaSrc: digitalAsset?.file?.public_id
          ? cloudinaryRef.getCloudinaryUrl(digitalAsset.file.public_id, {
              transformation: [{ format: 'jpg', crop: 'auto', width: 400 }],
            })
          : src || '',
        callToAction:
          share?.share_setting?.share_data?.share_data?.button_text || '',
        description:
          share?.share_setting?.post_format === 'template'
            ? share?.share_setting?.share_data?.share_data?.description || ''
            : share?.share_setting?.share_data?.share_data?.body || '',
        headline: share?.share_setting?.share_data?.share_data?.title || '',
      })
    } else {
      media.push({
        mediaType,
        mediaSrc: src || '',
      })
    }
  }

  // User/Avatar
  if (previewType === 'facebook/single') {
    if (share?.facebook_page?.name && share?.facebook_page?.picture_url) {
      userFullName = share.facebook_page.name
      userAvatar = share.facebook_page.picture_url
    }
  } else if (previewType === 'twitter') {
    if (
      share?.twitter_account?.name &&
      share?.twitter_account?.nickname &&
      share?.twitter_account?.profile_image_url
    ) {
      userFullName = share.twitter_account.name
      userHandle = share.twitter_account.nickname
      userAvatar = share.twitter_account.profile_image_url
    }
  }

  // Primary Text
  switch (previewType) {
    case 'facebook/single':
      if (share?.share_setting?.append_link_to_message === '1') {
        primaryText = `${(share && share.message) || ''} ${
          (share && share.retailer_url_override) || share?.share_setting?.link
        }`
      } else if (share?.message) {
        primaryText = share.message
      }
      break
    case 'instagram/single':
      primaryText = share?.message || primaryText
      break
    case 'twitter':
      primaryText = share?.tweet_text || share?.text || primaryText
      break
    case 'email':
      primaryText = share?.message || primaryText
      subject = share?.subject
      break
  }
  return {
    type: previewType,
    payload: {
      media,
      primaryText,
      shouldShowLink,
      userFullName,
      userHandle,
      userAvatar,
      subject,
    },
  } as SocialPreviewAdOptionsCommon<any>
}
const SocialPreview: React.FC<
  React.PropsWithChildren<{
    share: Share
  }>
> = (props) => {
  const previewRef = useRef(null)

  const socialPreviewOptions = useMemo(() => {
    return buildSocialPreviewOptions(props.share)
  }, [props.share])

  useEffect(() => {
    if (!previewRef.current || !socialPreviewOptions) {
      return
    }
    embedSocialPreview(previewRef.current, socialPreviewOptions)
  }, [socialPreviewOptions])

  return <div ref={previewRef} />
}

export default SocialPreview
